import { get } from './api';
// Temporarily using mock data - replace with real API calls when backend is ready
// import * as mockService from './adminService.mock';
import { User } from '../types/user';
import { Chat, Message } from '../types/chat';

export interface AdminUser extends User {
  chatsCount?: number;
  reportsCount?: number;
  lastActiveDate?: Date;
}

export interface SupportRequest {
  id: string;
  userId: string;
  title: string;
  description: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
  assignedTo?: string;
  userEmail?: string;
  userNickname?: string;
}

export interface UserCarReport {
  id: string;
  user_id: string;
  report_id?: string;
  title: string;
  plain_text_report?: string;
  pdf_url?: string;
  financial_summary?: any;
  gcs_path?: string;
  gcs_bucket?: string;
  url_expires_at?: string;
  created_at: string;
  updated_at: string;
  file_size?: number;
  generator_type?: string;
  template_name?: string;
  status: string;
  userEmail?: string;
  userNickname?: string;
}

export interface AdminStats {
  totalUsers: number;
  totalChats: number;
  totalReports: number;
  totalSupportRequests: number;
  activeUsers: number;
  newUsersToday: number;
  pendingSupportRequests: number;
}

// Get all users with admin info
export const getAllUsers = async (): Promise<AdminUser[]> => {
  try {
    return await get<AdminUser[]>('/admin/users');
  } catch (error) {
    console.error('Error getting users:', error);
    throw error;
  }
};

// Get user details with chats
export const getUserDetails = async (userId: string): Promise<{
  user: AdminUser;
  chats: Chat[];
  reports: UserCarReport[];
}> => {
  try {
    return await get<{
      user: AdminUser;
      chats: Chat[];
      reports: UserCarReport[];
    }>(`/admin/users/${userId}`);
  } catch (error) {
    console.error('Error getting user details:', error);
    throw error;
  }
};

// Get all support requests
export const getSupportRequests = async (): Promise<SupportRequest[]> => {
  try {
    return await get<SupportRequest[]>('/admin/support-requests');
  } catch (error) {
    console.error('Error getting support requests:', error);
    throw error;
  }
};

// Get admin dashboard stats
export const getAdminStats = async (): Promise<AdminStats> => {
  try {
    return await get<AdminStats>('/admin/stats');
  } catch (error) {
    console.error('Error getting admin stats:', error);
    throw error;
  }
};

// Get chat messages in timestamp order
export const getChatMessages = async (chatId: string): Promise<Message[]> => {
  try {
    return await get<Message[]>(`/admin/chats/${chatId}/messages`);
  } catch (error) {
    console.error('Error getting chat messages:', error);
    throw error;
  }
};

// Get analytics data for charts
export const getAnalyticsData = async (startDate: string, endDate: string): Promise<{
  dates: string[];
  newUsers: number[];
  activeUsers: number[];
}> => {
  try {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate
    });
    return await get<{
      dates: string[];
      newUsers: number[];
      activeUsers: number[];
    }>(`/admin/analytics?${params}`);
  } catch (error) {
    console.error('Error getting analytics data:', error);
    throw error;
  }
};

// For real-time updates, you could implement WebSocket or polling
export const subscribeToUsers = (callback: (users: AdminUser[]) => void) => {
  // Implement polling for real-time updates
  const interval = setInterval(async () => {
    try {
      const users = await getAllUsers();
      callback(users);
    } catch (error) {
      console.error('Error in user subscription:', error);
    }
  }, 30000); // Poll every 30 seconds

  // Return cleanup function
  return () => clearInterval(interval);
};